import { resolveFacilityContext } from "./Speak2Profile";

/**
 * Effective facility id for alexa-speak2-live requests.
 * Corporate: picker selection. Others: profile Facility.
 */
export async function resolveLambdaFacilityId(explicitFacilityId = null) {
  if (explicitFacilityId) return explicitFacilityId;
  const ctx = await resolveFacilityContext();
  return ctx?.facilityId ?? null;
}

/**
 * BE reads top-level selectedCorporateFacId when auth.User.Facility is null.
 * That's only true for corporate users (their own session has no facility —
 * they pick one via the corporate picker). Fixed: this used to accept any
 * facilityId, including a plain facility user's own profile-resolved
 * facilityId from resolveLambdaFacilityId() — sending it as
 * selectedCorporateFacId anyway for a non-corporate user (whose
 * auth.User.Facility is NOT null) sent the backend down a codepath it
 * wasn't meant to take for them, causing requests like the TV-slide reorder
 * endpoint to fail for regular facility admins. Use
 * resolveCorporateFacilityIdForRequest() to get a value that's only
 * non-null for an actual corporate user (or an explicit override), and pass
 * that here instead of a plain resolveLambdaFacilityId() result.
 */
export function withSelectedCorporateFacId(body = {}, facilityId) {
  if (!facilityId) return body;
  return {
    ...body,
    selectedCorporateFacId: facilityId,
  };
}

/**
 * Facility id to send as selectedCorporateFacId — only meaningful (and only
 * safe to send) for an actual corporate user. Requires BOTH `corporateId`
 * and `isCorporateUser` rather than either alone, because each one guards
 * against a different failure mode observed in speak2-live-backend:
 *
 * - `corporateId` matches the backend's own literal check:
 *   corporateAuth.setEffectiveFacility (services/src/utils/corporateAuth.js)
 *   only deletes `selectedCorporateFacId` from the request body when
 *   `context._Auth.User.CorporateId` is truthy — it doesn't look at role
 *   name or any flag. Left unchecked, a stray CorporateId on an otherwise
 *   non-corporate profile would still get the field sent.
 * - `isCorporateUser` (CorporateId + an active "Corporate Admin" role or an
 *   IsCorporateUser flag, see corporateFacilityStorage.js) approximates a
 *   SECOND backend gate we can't observe directly: setEffectiveFacility only
 *   even runs when `!authData.User?.Facility` (services/index.js:107) — i.e.
 *   the user's own backend session has no facility of its own. That's
 *   exactly the condition under which our frontend also resolves facilityId
 *   via the corporate picker instead of the profile's own Facility (see
 *   resolveFacilityContext). A user with a stale/inconsistent CorporateId
 *   who still has their own Facility server-side would fail this outer gate
 *   even though corporateId alone would pass, and vice versa.
 *
 * Either gap alone reproduces the original bug: the backend leaves
 * `selectedCorporateFacId` in the request body, and the endpoint's own Joi
 * schema (e.g. services/src/commands/signage/updateOrder.js's
 * bulkItemsSchema, with no `.unknown(true)`) rejects the undeclared key
 * outright ("selectedCorporateFacId is not allowed") — the actual mechanism
 * behind the TV-slide reorder failure. Requiring both closes both gaps.
 *
 * Many callers pass through whatever facility they already resolved for
 * other purposes (e.g. filtering), which is not necessarily corporate-picker
 * derived, so this always re-checks corporate status rather than trusting an
 * explicit facilityId argument on its own. A corporate user's own explicit
 * facility override (e.g. a specific sub-facility they picked) is still
 * respected; anyone who fails either check gets null regardless of what
 * facilityId was passed in, so withSelectedCorporateFacId becomes a no-op
 * for them instead of leaving a key the backend won't strip and the
 * endpoint won't accept.
 */
export async function resolveCorporateFacilityIdForRequest(explicitFacilityId = null) {
  const ctx = await resolveFacilityContext();
  if (!ctx?.corporateId || !ctx?.isCorporateUser) return null;
  return explicitFacilityId || ctx?.facilityId || null;
}

export function queryWithSelectedCorporateFacId(query = {}, facilityId) {
  if (!facilityId) return query;
  return {
    ...query,
    selectedCorporateFacId: facilityId,
  };
}

/** Merge Facility into Filter (legacy speak2 filter shape). */
export function withFacilityFilter(filter = {}, facilityId) {
  if (!facilityId) return filter;
  return { ...filter, Facility: facilityId };
}
